import * as React from 'react'
import { FormikHelpers } from 'formik'

import { Box, Button, Space } from '@ally/metronome-ui'
import {
  AccountNumber,
  Form,
  Forms,
  RadioButton,
  RadioGroup,
  VINHullId,
} from '@ally/forms'
import { AsyncStatus } from '@ally/transmitigator'

import { useTransmit } from '../../../TransmitContext'
import {
  CIAMErrorBanner,
  InputWrapper,
  LastFourTaxID,
  StyledTextBody,
} from '../../Shared'
import {
  FindAccountNumberModal,
  FindVINModal,
  WhyTaxIDModal,
} from '../../../Modals'
import { useHostServices } from '../../../HostServices'
import { FeatureFlag } from '../../../Utils'

interface BusinessForgotPasswordFormProps {
  error?: string | undefined
  isAutoBusinessUser: boolean
  status: AsyncStatus | 'Blocked' | 'Error'
  toggle: () => void
}
interface BusinessFormProps {
  accountNumber: string
  recoveryOption: RecoveryOption
  tin: string
  vin: string
}

type RecoveryOption = 'passwordHint' | 'createNewPassword'

const FORM_ID = 'business-forgot-password-form'

export const BusinessForgotPasswordForm: React.FC<BusinessForgotPasswordFormProps> = ({
  error,
  isAutoBusinessUser,
  status,
  toggle,
}) => {
  const { actions } = useTransmit()
  const [recoveryOptionSelection, setRecoveryOptionSelection] = React.useState<
    RecoveryOption
  >('passwordHint')
  const handleChange = (selected: string): void => {
    setRecoveryOptionSelection(selected as RecoveryOption)
  }
  const initialValues: BusinessFormProps = {
    accountNumber: '',
    recoveryOption: recoveryOptionSelection,
    tin: '',
    vin: '',
  }
  const { featureFlags } = useHostServices()
  const shouldShowRecoveryOptions = featureFlags.variation(
    FeatureFlag.BusinessForgotPWOptions,
    false,
  )

  const onSubmit = (
    { accountNumber, recoveryOption, tin, vin }: BusinessFormProps,
    { setSubmitting }: FormikHelpers<BusinessFormProps>,
  ): void => {
    const recoveryOptionChoices = {
      createNewPassword: recoveryOption === 'createNewPassword',
      passwordHint: recoveryOption === 'passwordHint',
    }

    actions.recoverPassword({
      accountNumber,
      tin,
      vin,
      isAutoBusinessUser,
      journey_version: 'v2',
      createNewPassword: true,
      passwordHint: false,
      ...(shouldShowRecoveryOptions ? recoveryOptionChoices : {}),
    })
    // NOTE: this allows for form resubmission after failure
    setSubmitting(false)
  }

  return (
    <>
      <Space mb="md">
        <StyledTextBody size="sm" tag="p">
          To recover your password, we need some info about your auto account.
        </StyledTextBody>
      </Space>

      <Space mb="md">
        <Button
          onClick={toggle}
          text="Don’t have an auto business account?"
          type="button"
          variant="link"
        />
      </Space>

      <CIAMErrorBanner mb="md" error={error} />

      <Forms formId={FORM_ID} initialValues={initialValues} onSubmit={onSubmit}>
        <Form id={FORM_ID}>
          <InputWrapper mb="md">
            <LastFourTaxID name="tin" />

            <Box mt="sm" mb="xl">
              <WhyTaxIDModal allytmln="forgotPassword-whyDoINeedMyTaxID" />
            </Box>
          </InputWrapper>
          <InputWrapper mb="md">
            <AccountNumber isPrivate name="accountNumber" />

            <Box mt="sm" mb="xl">
              <FindAccountNumberModal allytmln="forgotPassword-whereCanIFindMyAccountNumber" />
            </Box>
          </InputWrapper>
          <InputWrapper mb="md">
            <VINHullId isPrivate name="vin" />

            <Box mt="sm" mb="xl">
              <FindVINModal allytmln="forgotPassword-whereCanIFindMyVIN" />
            </Box>
          </InputWrapper>

          {shouldShowRecoveryOptions && (
            <RadioGroup
              legendText="Password recovery options"
              isLegendHidden={false}
              name="recoveryOption"
              data-allytmfn="recoveryOption"
              defaultValue={recoveryOptionSelection}
              onChange={handleChange}
            >
              <RadioButton
                label="Get a password hint by email or text (based on your delivery preference)"
                value="passwordHint"
              />
              <RadioButton
                label="Create a new password"
                value="createNewPassword"
              />
            </RadioGroup>
          )}

          <Button
            allytmln="forgotPassword-accountInfo-continue"
            type="submit"
            variant="primary"
            text="Continue"
            loading={status === 'Loading'}
          />
        </Form>
      </Forms>
    </>
  )
}
